import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { RefreshCw, Upload, Move, Image, X, ChevronDown, ChevronUp, Edit3, Check, Scissors, Sparkles, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LogoPlacementEditor from './LogoPlacementEditor';
import TimestampedNotes from './TimestampedNotes';
import VectorDesignEditor from './VectorDesignEditor';
import LiveMockupCanvas from './LiveMockupCanvas';
import ColorSeparationSheet from './ColorSeparationSheet';
import { ZoomableImage } from './MockupLightbox';

const DECORATION_METHODS = [
  { value: 'screen_print', label: 'Screen Print' },
  { value: 'embroidery', label: 'Embroidery' },
  { value: 'dtf', label: 'DTF Transfer' },
  { value: 'dtg', label: 'DTG' },
  { value: 'sublimation', label: 'Sublimation' },
  { value: 'heat_transfer', label: 'Heat Transfer' },
  { value: 'vinyl', label: 'Vinyl' },
];
const DEFAULT_LOCATIONS = ['Front', 'Back', 'Left Sleeve', 'Right Sleeve', 'Left Chest', 'Right Chest'];

const GARMENT_TYPES = [
  { value: 't-shirt', label: 'T-Shirt' },
  { value: 'polo', label: 'Polo Shirt' },
  { value: 'hoodie', label: 'Hoodie' },
  { value: 'long_sleeve', label: 'Long Sleeve' },
  { value: 'tank_top', label: 'Tank Top' },
  { value: 'jacket', label: 'Jacket' }
];

const ProductMockupCard = ({ item, itemIndex, proposalId, onUpdate, customerEmail = null }) => {
  const [expanded, setExpanded] = useState(false);
  const [sepOpen, setSepOpen] = useState(false);  // Color Separation Sheet modal
  const [generating, setGenerating] = useState(false);
  const [showLogoUpload, setShowLogoUpload] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedGarmentType, setSelectedGarmentType] = useState(item.garment_type || 't-shirt');
  const [selectedColor, setSelectedColor] = useState(item.color || 'Black');
  const [selectedView, setSelectedView] = useState('front');
  const navigate = useNavigate();

  // Externalized mockup state: when an item has a `mockup_design_id`, the heavy 4-view
  // composites live in the `mockup_designs` collection (not inline on the proposal item,
  // to stay under MongoDB's 16MB BSON cap). Fetch them on mount and re-fetch when the
  // design id changes.
  const [designerViews, setDesignerViews] = useState(null);
  const [designerThumb, setDesignerThumb] = useState(null);

  // ===== Saved designs picker (per-customer auto-suggest from /designs/customer-suggest) =====
  const [savedDesigns, setSavedDesigns] = useState([]);
  const [savedDesignsOpen, setSavedDesignsOpen] = useState(false);
  const [attachingDesignId, setAttachingDesignId] = useState(null);
  useEffect(() => {
    // Only fetch once and only when we have an email — keeps the proposal page snappy.
    if (!customerEmail) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get('/designs/customer-suggest', { params: { customer_email: customerEmail } });
        if (!cancelled) setSavedDesigns(r.data || []);
      } catch { /* non-fatal: no saved designs is a valid state */ }
    })();
    return () => { cancelled = true; };
  }, [customerEmail]);

  const attachSavedDesign = async (designId) => {
    setAttachingDesignId(designId);
    try {
      const r = await axios.post(`/designs/${designId}/attach`, {
        proposal_id: proposalId,
        item_index: itemIndex,
      });
      if (r.data?.attached) {
        toast.success('Saved design attached');
        // Fetch the full design (with image) so we can preview + push to parent.
        try {
          const full = await axios.get(`/designs/${designId}`);
          const raw = (full.data?.image_base64 || '').replace(/^data:[^;]+;base64,/, '');
          if (onUpdate) onUpdate(itemIndex, { ...item, logo_base64: raw });
        } catch { /* parent will re-fetch on next refresh */ }
        setSavedDesignsOpen(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not attach design');
    } finally {
      setAttachingDesignId(null);
    }
  };

  // Save the item's currently-attached logo to the Design Library so it can be
  // reused on future proposals for this customer. The logo may live in two
  // places: directly on the item (`logo_base64`) OR inside the Mockup Designer's
  // saved state (first image layer in `mockup_designs.sides.front.layers`).
  // We try both; the button shows whenever either source has data.
  const [savingToLib, setSavingToLib] = useState(false);

  const findLogoForLibrary = async () => {
    if (item.logo_base64) {
      return item.logo_base64.startsWith('data:') ? item.logo_base64 : `data:image/png;base64,${item.logo_base64}`;
    }
    // Fallback: pull the first image layer from the saved mockup design.
    if (item.mockup_design_id || true) {
      try {
        const r = await axios.get(`/proposals/${proposalId}/items/${itemIndex}/mockup-design`);
        const layers = r.data?.sides?.front?.layers || [];
        const imgLayer = layers.find((l) => l.type === 'image' && l.src);
        if (imgLayer) {
          return imgLayer.src.startsWith('data:') ? imgLayer.src : `data:image/png;base64,${imgLayer.src}`;
        }
      } catch { /* fall through */ }
    }
    return null;
  };
  const hasLogoOnItem = !!item.logo_base64 || !!item.mockup_design_id;

  const saveLogoToLibrary = async () => {
    const src = await findLogoForLibrary();
    if (!src) {
      toast.error('No logo on this item yet — upload one or attach a saved design first');
      return;
    }
    const suggested = `${(item.brand || '').trim()} ${(item.style || '').trim()} logo`.trim() || 'Saved logo';
    const name = window.prompt('Name this design for the library:', suggested);
    if (!name) return;
    setSavingToLib(true);
    try {
      const r = await axios.post('/designs', {
        name: name.trim(),
        image_base64: src,
        customer_email: customerEmail || null,
        customer_name: item.customer_name || null,
        decoration_notes: item.decoration_notes || item.design_description || null,
        print_method: item.print_method || 'screen_print',
        tags: [],
      });
      toast.success('Saved to library');
      try {
        if (customerEmail) {
          const s = await axios.get('/designs/customer-suggest', { params: { customer_email: customerEmail } });
          setSavedDesigns(s.data || []);
        }
      } catch { /* non-fatal */ }
      if (r.data?.id) {
        try {
          await axios.post(`/designs/${r.data.id}/attach`, { proposal_id: proposalId, item_index: itemIndex });
        } catch { /* non-fatal */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save design');
    } finally {
      setSavingToLib(false);
    }
  };

  // ===== Decoration notes (per-item production memo, travels with the item) =====
  // The new TimestampedNotes component manages its own state — we keep this
  // tiny effect only to back-fill the legacy `decoration_notes` string into a
  // single log entry on first load, so existing data is not lost.
  useEffect(() => {
    const log = item.production_notes_log;
    const legacy = item.decoration_notes || item.design_description;
    if ((!log || log.length === 0) && legacy && proposalId != null && itemIndex != null) {
      // Best-effort migration; ignore failures silently.
      axios.post(`/proposals/${proposalId}/items/${itemIndex}/production-note`,
        { text: legacy })
        .then(r => {
          if (onUpdate && r.data?.entry) {
            onUpdate(itemIndex, {
              ...item,
              production_notes_log: [r.data.entry],
              decoration_notes: legacy,
            });
          }
        })
        .catch(() => { /* non-fatal */ });
    }
  }, [item.id]); // eslint-disable-line

  // ===== Admin approval =====
  const approvalStatus = item.approval_status || null; // 'approved' | 'changes_requested' | 'pending' | null
  const [approving, setApproving] = useState(false);
  const handleAdminApprove = async (e) => {
    if (e) e.stopPropagation();
    setApproving(true);
    try {
      const r = await axios.post(`/proposals/${proposalId}/items/${itemIndex}/admin-approve`);
      toast.success(r.data?.order_created ? 'Approved · order created' : 'Approved');
      if (onUpdate) onUpdate(itemIndex, { ...item, approval_status: 'approved', approved_at: new Date().toISOString() });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  // ===== Decoration editor (per-item, per-location method) =====
  const [decoOpen, setDecoOpen] = useState(false);
  const [decoPrimary, setDecoPrimary] = useState(item.print_method || 'screen_print');
  const [decoLocations, setDecoLocations] = useState(
    Array.isArray(item.print_locations) && item.print_locations.length ? item.print_locations : ['Front']
  );
  const [decoLocationMethods, setDecoLocationMethods] = useState(item.location_methods || {});
  const [decoColors, setDecoColors] = useState(item.print_colors || {});
  const [decoStitch, setDecoStitch] = useState(item.stitch_count || 8000);
  const [savingDeco, setSavingDeco] = useState(false);
  const [confirmReprice, setConfirmReprice] = useState(false); // shown after pressing Save

  const openDecoration = (e) => {
    if (e) e.stopPropagation();
    // Re-sync from latest item props in case parent updated.
    setDecoPrimary(item.print_method || 'screen_print');
    setDecoLocations(Array.isArray(item.print_locations) && item.print_locations.length ? item.print_locations : ['Front']);
    setDecoLocationMethods(item.location_methods || {});
    setDecoColors(item.print_colors || {});
    setDecoStitch(item.stitch_count || 8000);
    setDecoOpen(true);
  };

  const toggleDecoLocation = (loc) => {
    setDecoLocations((prev) => (prev.includes(loc) ? prev.filter((l) => l !== loc) : [...prev, loc]));
  };

  const persistDecoration = async (reprice) => {
    setSavingDeco(true);
    try {
      const body = {
        print_method: decoPrimary,
        print_locations: decoLocations,
        location_methods: decoLocationMethods,
        print_colors: decoColors,
        stitch_count: parseInt(decoStitch) || 8000,
        reprice,
      };
      const r = await axios.patch(`/proposals/${proposalId}/items/${itemIndex}/decoration`, body);
      toast.success(reprice ? 'Decoration updated · proposal repriced' : 'Decoration updated');
      if (onUpdate) {
        onUpdate(itemIndex, { ...item, ...body });
      }
      setDecoOpen(false);
      setConfirmReprice(false);
      // If repriced, the parent should refresh totals; bubble the full proposal up.
      if (reprice && r.data?.proposal && typeof onUpdate === 'function') {
        // signal a full refresh via a sentinel index = -1 if parent supports it
        onUpdate(-1, r.data.proposal);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save decoration');
    } finally {
      setSavingDeco(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchDesign = async () => {
      if (!proposalId) return;
      try {
        const r = await axios.get(`/proposals/${proposalId}/items/${itemIndex}/mockup-design`);
        if (cancelled) return;
        const views = r.data?.mockup_views || null;
        if (views && Object.keys(views).length > 0) {
          setDesignerViews(views);
          setDesignerThumb(views.front || Object.values(views)[0]);
        } else {
          setDesignerViews(null);
          setDesignerThumb(null);
        }
      } catch (e) {
        // No design saved yet — that's fine.
        if (!cancelled) { setDesignerViews(null); setDesignerThumb(null); }
      }
    };
    fetchDesign();
    return () => { cancelled = true; };
  }, [proposalId, itemIndex, item.mockup_design_id, item.mockup_url]);

  // Generate mockup for this specific item
  const generateItemMockup = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`/proposals/${proposalId}/items/${itemIndex}/generate-mockup`, {
        garment_type: selectedGarmentType,
        shirt_color: selectedColor,
        logo_base64: item.logo_base64 || logoPreview
      });
      
      if (onUpdate) {
        onUpdate(itemIndex, {
          ...item,
          mockup_views: response.data.mockup_views,
          base_mockup_views: response.data.base_mockup_views,
          garment_type: selectedGarmentType
        });
      }
      
      toast.success(`Mockup generated for ${item.brand} ${item.style || item.styleID}`);
    } catch (error) {
      toast.error('Failed to generate mockup');
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  // Handle logo upload for this item
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setLogoPreview(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  // Apply logo to this item's mockup
  const applyLogoToItem = async (position) => {
    if (!logoPreview && !item.logo_base64) {
      toast.error('Please upload a logo first');
      return;
    }

    try {
      const response = await axios.post(`/proposals/${proposalId}/items/${itemIndex}/apply-logo`, {
        logo_base64: logoPreview || item.logo_base64,
        view: selectedView,
        ...position
      });
      
      if (onUpdate) {
        onUpdate(itemIndex, {
          ...item,
          mockup_views: {
            ...item.mockup_views,
            [selectedView]: response.data.mockup
          },
          logo_base64: logoPreview || item.logo_base64,
          logo_positions: {
            ...item.logo_positions,
            [selectedView]: position
          }
        });
      }
      
      setShowLogoUpload(false);
      toast.success('Logo applied!');
    } catch (error) {
      toast.error('Failed to apply logo');
    }
  };

  const mockupViews = designerViews || item.mockup_views || {};
  const hasViews = Object.keys(mockupViews).length > 0;
  const VIEW_LABELS = { front: 'Front', back: 'Back', left: 'Left Sleeve', right: 'Right Sleeve' };
  const VIEW_KEYS = ['front', 'back', 'left', 'right'];

  // Normalize a view src to a data: URI. The mockup designer writes data: URIs directly,
  // the legacy generator returned raw base64. Handle both.
  const viewSrc = (v) => {
    if (!v) return null;
    return v.startsWith?.('data:') ? v : `data:image/png;base64,${v}`;
  };

  return (
    <div style={{ 
      border: '1px solid #e2e8f0', 
      borderRadius: '12px', 
      marginBottom: '16px',
      overflow: 'hidden'
    }}>
      {/* Header - Always visible */}
      <div
        data-testid={`product-mockup-card-header-${itemIndex}`}
        style={{ 
          padding: '16px', 
          background: '#f7fafc', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {(designerThumb || hasViews) ? (
            <img 
              src={viewSrc(designerThumb || mockupViews.front || Object.values(mockupViews)[0])}
              alt="Mockup thumbnail"
              style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '6px', background: '#fff' }}
            />
          ) : item.mockup_url ? (
            <img 
              src={item.mockup_url}
              alt="Mockup thumbnail"
              style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '6px', background: '#fff' }}
            />
          ) : (
            <div style={{ 
              width: '50px', 
              height: '50px', 
              background: '#e2e8f0', 
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Image size={20} color="#a0aec0" />
            </div>
          )}
          <div>
            <h4 style={{ fontWeight: '600', fontSize: '15px', margin: 0 }}>
              {item.brand} - {item.style || item.styleID}
            </h4>
            <p style={{ fontSize: '12px', color: '#718096', margin: 0 }}>
              {item.color} • Qty: {item.quantity} • {item.garment_type || 'Not set'}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {approvalStatus === 'approved' && (
            <span style={{ fontSize: 11, padding: '2px 8px', background: '#dcfce7', color: '#16a34a', borderRadius: 4, fontWeight: 600 }}
              data-testid={`approved-pill-${itemIndex}`}>
              ✓ Approved
            </span>
          )}
          {approvalStatus === 'changes_requested' && (
            <span style={{ fontSize: 11, padding: '2px 8px', background: '#fef3c7', color: '#a16207', borderRadius: 4, fontWeight: 600 }}>
              ⚠ Changes Requested
            </span>
          )}
          {item.logo_base64 && (
            <span style={{ 
              fontSize: '11px', 
              padding: '2px 8px', 
              background: '#dcfce7', 
              color: '#16a34a',
              borderRadius: '4px'
            }}>
              Has Logo
            </span>
          )}
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {/* Per-location Logo Sizes — admin-editable. Stamps the print
          dimensions onto each item so the customer's approval covers BOTH
          the design and the dimensions. Renders only if the item has
          locations (or as a single "Front" row when not specified). */}
      <PrintSizesEditor item={item} itemIndex={itemIndex} proposalId={proposalId} onUpdated={onUpdate} />

      {/* Decoration / Production Notes — time-stamped, append-only log. Each
          entry preserves who said what when, so the production floor and ops
          always have full historical context. */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', background: '#fffbeb' }}
        data-testid={`decoration-notes-${itemIndex}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            🧵 Decoration / Production Notes
          </span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {hasLogoOnItem && (
              <Button size="sm" variant="ghost"
                onClick={(e) => { e.stopPropagation(); saveLogoToLibrary(); }}
                disabled={savingToLib}
                data-testid={`save-to-library-${itemIndex}`}
                title="Save this item's current logo to the Design Library for future reuse"
                style={{ color: '#16a34a', fontSize: 11, height: 24 }}>
                <Sparkles size={11} style={{ marginRight: 4 }} />
                {savingToLib ? 'Saving…' : 'Save to Library'}
              </Button>
            )}
            {savedDesigns.length > 0 && (
              <Button size="sm" variant="ghost"
                onClick={(e) => { e.stopPropagation(); setSavedDesignsOpen(true); }}
                data-testid={`use-saved-design-${itemIndex}`}
                title="Pull a saved design for this customer"
                style={{ color: '#7c3aed', fontSize: 11, height: 24 }}>
                <Sparkles size={11} style={{ marginRight: 4 }} />Use saved design ({savedDesigns.length})
              </Button>
            )}
          </div>
        </div>
        <TimestampedNotes
          entries={item.production_notes_log || []}
          addUrl={`/proposals/${proposalId}/items/${itemIndex}/production-note`}
          deleteUrlPrefix={`/proposals/${proposalId}/items/${itemIndex}/production-note`}
          placeholder={`e.g. Front embroidery · Panther head full color (Gold 1672, Navy 1743, Sand 1684, White) · ~5" wide · 10K stitches.`}
          title="Notes log"
          accentColor="#92400e"
          testIdPrefix={`item-prod-notes-${itemIndex}`}
          onChange={({ entries, latestText }) => {
            if (onUpdate) onUpdate(itemIndex, {
              ...item,
              production_notes_log: entries,
              decoration_notes: latestText,
            });
          }}
        />
      </div>

      {/* All-views preview strip — visible inline whether expanded or not. Hidden if no design saved. */}
      {hasViews && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', background: '#fff' }}
          data-testid={`views-strip-${itemIndex}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              All 4 views
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button size="sm" variant="ghost"
                onClick={openDecoration}
                title="Set decoration method per location"
                style={{ color: '#0f766e', fontSize: 11, height: 24 }}
                data-testid={`decoration-btn-${itemIndex}`}>
                <Scissors size={11} style={{ marginRight: 4 }} />Decoration
              </Button>
              {approvalStatus !== 'approved' && (
                <Button size="sm" variant="ghost"
                  onClick={handleAdminApprove}
                  disabled={approving}
                  title="Approve this mockup on the customer's behalf"
                  style={{ color: '#16a34a', fontSize: 11, height: 24 }}
                  data-testid={`admin-approve-${itemIndex}`}>
                  <Check size={11} style={{ marginRight: 4 }} />{approving ? 'Approving…' : 'Approve'}
                </Button>
              )}
              <Button size="sm" variant="ghost"
                onClick={(e) => { e.stopPropagation(); navigate(`/mockup-designer?proposal=${proposalId}&item=${itemIndex}&regenerate=1`); }}
                title="Re-render saved composites with the latest aspect-ratio fix"
                style={{ color: '#0ea5e9', fontSize: 11, height: 24 }}
                data-testid={`refresh-mockup-${itemIndex}`}>
                <RefreshCw size={11} style={{ marginRight: 4 }} />Re-render
              </Button>
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/mockup-designer?proposal=${proposalId}&item=${itemIndex}`); }}
                style={{ color: '#7c3aed', fontSize: 11, height: 24 }}
                data-testid={`edit-design-${itemIndex}`}>
                <Edit3 size={11} style={{ marginRight: 4 }} />Edit
              </Button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {VIEW_KEYS.map((vk) => {
              const src = mockupViews[vk];
              return (
                <div key={vk} style={{
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                  overflow: 'hidden', textAlign: 'center',
                }}>
                  {/* Square 1:1 box for the photo — keeps the saved composite undistorted */}
                  <div style={{ position: 'relative', width: '100%', paddingBottom: '100%', background: '#ffffff' }}>
                    {src ? (
                      <ZoomableImage
                        src={viewSrc(src)}
                        alt={VIEW_LABELS[vk]}
                        title={`${VIEW_LABELS[vk]} mockup`}
                        style={{ position: 'absolute', inset: 0 }}
                        imgStyle={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                        testId={`zoomable-view-${itemIndex}-${vk}`}
                      />
                    ) : (
                      <div style={{
                        position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        color: '#94a3b8', fontSize: 10, padding: 8, textAlign: 'center',
                      }}>
                        No {VIEW_LABELS[vk].toLowerCase()}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '4px 6px', fontSize: 10, color: '#475569', fontWeight: 600, borderTop: '1px solid #e2e8f0' }}>
                    {VIEW_LABELS[vk]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Expanded Content */}
      {expanded && (
        <div style={{ padding: '16px' }}>
          {/* Controls */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <Select value={selectedGarmentType} onValueChange={setSelectedGarmentType}>
              <SelectTrigger style={{ width: '140px' }}>
                <SelectValue placeholder="Garment type" />
              </SelectTrigger>
              <SelectContent>
                {GARMENT_TYPES.map(g => (
                  <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {item.colors && item.colors.length > 0 ? (
              <Select value={selectedColor} onValueChange={setSelectedColor}>
                <SelectTrigger style={{ width: '120px' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {item.colors.map((c, idx) => (
                    <SelectItem key={idx} value={c.colorName || c}>{c.colorName || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span style={{ padding: '8px 12px', background: '#f7fafc', borderRadius: '6px', fontSize: '14px' }}>
                {selectedColor}
              </span>
            )}

            <Button 
              size="sm" 
              onClick={generateItemMockup}
              disabled={generating}
            >
              <RefreshCw size={14} className={`mr-2 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating...' : 'Generate Mockup'}
            </Button>
          </div>

          {/* Mockup Views */}
          {hasViews ? (
            <div>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {Object.entries(mockupViews).map(([view, imageData]) => (
                  <div 
                    key={view} 
                    style={{ 
                      textAlign: 'center',
                      border: selectedView === view ? '2px solid #667eea' : '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      width: 160,
                    }}
                    onClick={() => setSelectedView(view)}
                  >
                    {/* Square 1:1 frame so composites render undistorted */}
                    <div style={{ position: 'relative', width: '100%', paddingBottom: '100%', background: '#ffffff', borderRadius: 4 }}>
                      <ZoomableImage
                        src={viewSrc(imageData)}
                        alt={`${view} view`}
                        title={`${view} mockup`}
                        style={{ position: 'absolute', inset: 0 }}
                        imgStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        testId={`zoomable-thumb-${itemIndex}-${view}`}
                      />
                    </div>
                    <p style={{ fontSize: '12px', fontWeight: '500', marginTop: '4px', textTransform: 'uppercase' }}>
                      {view}
                    </p>
                  </div>
                ))}
              </div>

              {/* Logo Actions */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowLogoUpload(true)}
                >
                  <Move size={14} className="mr-2" />
                  Position Logo on {selectedView}
                </Button>
                {/* Color Separations — only meaningful for screen-print items
                    with a real vector design uploaded. The sheet opens a 17×36
                    art board with one B&W channel per spot color, halftone-
                    flagged channels marked, and Print/Save SVG buttons. */}
                {(item.print_method || '').toLowerCase() === 'screen_print' &&
                  item.vector_design && item.vector_design.format === 'svg' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSepOpen(true)}
                      data-testid={`open-color-separations-${itemIndex}`}
                    >
                      <Layers size={14} className="mr-2" />
                      Color Separations
                    </Button>
                )}
              </div>

              {/* Vector Design (SVG color editor + AI/EPS preview).
                  Lives next to the mockup so the admin can upload a logo
                  file once and have the recolored SVG flow through to the
                  customer payment page + the production-pack PDF. */}
              <div style={{ marginTop: '16px' }} data-testid={`vector-design-section-${itemIndex}`}>
                <p style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  Vector Design (logo file)
                </p>
                {/* Live mockup canvases: one per saved placement. Admin can
                    drag the design and tweak size/rotation; changes commit
                    via the same PATCH endpoint the customer uses. */}
                {item.vector_design && item.vector_design.format === 'svg' && (() => {
                  const vd = item.vector_design;
                  const placements = vd.placements || {};
                  const locs = Object.keys(placements).length
                    ? Object.keys(placements)
                    : ['front'];
                  const mockupSrcFor = (loc) => mockupViews[loc] || mockupViews[loc.replace('_sleeve','')] || null;
                  const savePlacement = async (loc, next) => {
                    try {
                      const r = await axios.patch(
                        `/proposals/${proposalId}/items/${itemIndex}/vector-design`,
                        { placements: { [loc]: next } }
                      );
                      if (onUpdate) onUpdate(itemIndex, { ...item, vector_design: r.data.vector_design });
                    } catch (e) {
                      toast.error('Failed to save placement');
                    }
                  };
                  return (
                    <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: locs.length > 1 ? 'repeat(auto-fit, minmax(220px, 1fr))' : '1fr' }}>
                      {locs.map((loc) => (
                        <LiveMockupCanvas
                          key={loc}
                          baseSrc={mockupSrcFor(loc) ? viewSrc(mockupSrcFor(loc)) : null}
                          svgContent={vd.svg_content}
                          placement={placements[loc] || { x: 50, y: 35, scale: 25, rotation: 0 }}
                          locationLabel={loc.replace(/_/g, ' ')}
                          editable={true}
                          onPlacementChange={(next) => savePlacement(loc, next)}
                          testIdPrefix={`live-mockup-${itemIndex}-${loc}`}
                        />
                      ))}
                    </div>
                  );
                })()}
                <VectorDesignEditor
                  vectorDesign={item.vector_design || null}
                  proposalId={proposalId}
                  itemIndex={itemIndex}
                  mode="admin"
                  locations={(item.print_locations || ['front', 'back']).map((l) =>
                    String(l).toLowerCase().replace(/\s+/g, '_')
                  )}
                  onUpdate={(vd) => {
                    if (onUpdate) {
                      onUpdate(itemIndex, { ...item, vector_design: vd });
                    }
                  }}
                />
              </div>

              {/* Logo Upload/Position Modal */}
              {showLogoUpload && (
                <div style={{ 
                  marginTop: '16px', 
                  background: '#1a1a2e', 
                  borderRadius: '12px', 
                  padding: '20px'
                }}>
                  {!logoPreview && !item.logo_base64 ? (
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ color: '#a0aec0', marginBottom: '12px' }}>
                        Upload a logo for this product
                      </p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                        id={`logo-upload-${itemIndex}`}
                      />
                      <label 
                        htmlFor={`logo-upload-${itemIndex}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '10px 20px',
                          background: '#667eea',
                          color: 'white',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                      >
                        <Upload size={16} className="mr-2" />
                        Choose Logo File
                      </label>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setShowLogoUpload(false)}
                        style={{ marginLeft: '8px', color: '#a0aec0' }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <LogoPlacementEditor
                      mockupBase64={item.base_mockup_views?.[selectedView] || mockupViews[selectedView]}
                      logoBase64={logoPreview || item.logo_base64}
                      initialPosition={item.logo_positions?.[selectedView] || { x: 50, y: 35, width: 30, height: 30, rotation: 0 }}
                      onSave={applyLogoToItem}
                      onCancel={() => {
                        setShowLogoUpload(false);
                        setLogoPreview(null);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              background: '#f7fafc', 
              borderRadius: '8px' 
            }}>
              <Image size={40} color="#a0aec0" style={{ marginBottom: '12px' }} />
              <p style={{ color: '#718096', marginBottom: '16px' }}>
                No mockup generated for this product yet
              </p>
              <Button onClick={generateItemMockup} disabled={generating}>
                <RefreshCw size={14} className={`mr-2 ${generating ? 'animate-spin' : ''}`} />
                Generate Mockup
              </Button>

              {/* Color Separations also available pre-mockup so the production
                  team can start prepping films before the t-shirt mockup is
                  generated. Same gate: screen_print + SVG vector design. */}
              {(item.print_method || '').toLowerCase() === 'screen_print' &&
                item.vector_design && item.vector_design.format === 'svg' && (
                  <Button
                    variant="outline"
                    onClick={() => setSepOpen(true)}
                    style={{ marginLeft: 8 }}
                    data-testid={`open-color-separations-${itemIndex}`}
                  >
                    <Layers size={14} className="mr-2" />
                    Color Separations
                  </Button>
              )}

              {/* Vector design upload is available even without a mockup
                  so the admin can attach the customer's logo file first
                  and let the design flow into the mockup generator. */}
              <div style={{ marginTop: '24px', textAlign: 'left' }} data-testid={`vector-design-section-${itemIndex}`}>
                <p style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  Vector Design (logo file)
                </p>
                {/* Same live-mockup preview as the hasViews branch, but with
                    no base image — admin can still drag/scale to lay out the
                    print spec before generating the t-shirt mockup. */}
                {item.vector_design && item.vector_design.format === 'svg' && (() => {
                  const vd = item.vector_design;
                  const placements = vd.placements || {};
                  const locs = Object.keys(placements).length
                    ? Object.keys(placements)
                    : ['front'];
                  const savePlacement = async (loc, next) => {
                    try {
                      const r = await axios.patch(
                        `/proposals/${proposalId}/items/${itemIndex}/vector-design`,
                        { placements: { [loc]: next } }
                      );
                      if (onUpdate) onUpdate(itemIndex, { ...item, vector_design: r.data.vector_design });
                    } catch (e) {
                      toast.error('Failed to save placement');
                    }
                  };
                  return (
                    <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: locs.length > 1 ? 'repeat(auto-fit, minmax(220px, 1fr))' : '1fr' }}>
                      {locs.map((loc) => (
                        <LiveMockupCanvas
                          key={loc}
                          baseSrc={null}
                          svgContent={vd.svg_content}
                          placement={placements[loc] || { x: 50, y: 35, scale: 25, rotation: 0 }}
                          locationLabel={loc.replace(/_/g, ' ')}
                          editable={true}
                          onPlacementChange={(next) => savePlacement(loc, next)}
                          testIdPrefix={`live-mockup-${itemIndex}-${loc}`}
                        />
                      ))}
                    </div>
                  );
                })()}
                <VectorDesignEditor
                  vectorDesign={item.vector_design || null}
                  proposalId={proposalId}
                  itemIndex={itemIndex}
                  mode="admin"
                  locations={(item.print_locations || ['front', 'back']).map((l) =>
                    String(l).toLowerCase().replace(/\s+/g, '_')
                  )}
                  onUpdate={(vd) => {
                    if (onUpdate) {
                      onUpdate(itemIndex, { ...item, vector_design: vd });
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Saved-design picker — auto-suggested past designs for this customer */}
      <Dialog open={savedDesignsOpen} onOpenChange={setSavedDesignsOpen}>
        <DialogContent style={{ maxWidth: 560 }} data-testid={`saved-designs-dialog-${itemIndex}`}>
          <DialogHeader>
            <DialogTitle>Use a saved design</DialogTitle>
            <DialogDescription>
              Past artwork uploaded for this customer. One click attaches it as the logo on this item.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, paddingTop: 6, maxHeight: 480, overflowY: 'auto' }}>
            {savedDesigns.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => attachSavedDesign(d.id)}
                disabled={!!attachingDesignId}
                data-testid={`saved-design-tile-${d.id}`}
                style={{
                  all: 'unset', cursor: attachingDesignId ? 'wait' : 'pointer',
                  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 8, textAlign: 'left',
                  opacity: attachingDesignId && attachingDesignId !== d.id ? 0.4 : 1,
                }}
              >
                <div style={{ width: '100%', paddingBottom: '100%', background: '#f8fafc', borderRadius: 6, display: 'grid', placeItems: 'center', color: '#cbd5e1', fontSize: 10 }}>
                  preview
                </div>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', margin: '6px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {attachingDesignId === d.id ? 'Attaching…' : d.name}
                </p>
                <p style={{ fontSize: 10, color: '#94a3b8', margin: '2px 0 0' }}>
                  {d.print_method ? d.print_method.replace('_', ' ').toUpperCase() : '—'} · {d.used_in_proposal_ids?.length || 0} uses
                </p>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSavedDesignsOpen(false)} disabled={!!attachingDesignId}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decoration editor dialog */}
      <Dialog open={decoOpen} onOpenChange={setDecoOpen}>
        <DialogContent style={{ maxWidth: 560 }} data-testid={`decoration-dialog-${itemIndex}`}>
          <DialogHeader>
            <DialogTitle>Decoration · {item.brand} {item.style || item.styleID}</DialogTitle>
            <DialogDescription>
              Set the method per print location. Useful for hats that go embroidery on the front but screen print on the back.
            </DialogDescription>
          </DialogHeader>

          <div style={{ display: 'grid', gap: 16, paddingTop: 4 }}>
            <div>
              <Label style={{ fontSize: 12, fontWeight: 600 }}>Default method (used for any location without an override)</Label>
              <Select value={decoPrimary} onValueChange={setDecoPrimary}>
                <SelectTrigger style={{ marginTop: 6 }} data-testid={`deco-primary-${itemIndex}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DECORATION_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label style={{ fontSize: 12, fontWeight: 600 }}>Print locations</Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {DEFAULT_LOCATIONS.map((loc) => {
                  const active = decoLocations.includes(loc);
                  return (
                    <button key={loc} type="button"
                      onClick={() => toggleDecoLocation(loc)}
                      data-testid={`deco-loc-${itemIndex}-${loc}`}
                      style={{
                        padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                        border: '1px solid', cursor: 'pointer',
                        background: active ? '#0f766e' : '#ffffff',
                        color: active ? '#ffffff' : '#475569',
                        borderColor: active ? '#0f766e' : '#cbd5e1',
                      }}>
                      {loc}
                    </button>
                  );
                })}
              </div>
            </div>

            {decoLocations.length > 0 && (
              <div>
                <Label style={{ fontSize: 12, fontWeight: 600 }}>Per-location method</Label>
                <div style={{ display: 'grid', gap: 8, marginTop: 6 }}>
                  {decoLocations.map((loc) => {
                    const method = decoLocationMethods[loc] || decoPrimary;
                    return (
                      <div key={loc} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 100px', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 13, color: '#475569' }}>{loc}</span>
                        <Select value={method} onValueChange={(v) => setDecoLocationMethods((prev) => ({ ...prev, [loc]: v }))}>
                          <SelectTrigger data-testid={`deco-loc-method-${itemIndex}-${loc}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DECORATION_METHODS.map((m) => (
                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {method === 'screen_print' ? (
                          <Input type="number" min={1} max={8} value={decoColors[loc] || 1}
                            placeholder="Colors"
                            onChange={(e) => setDecoColors((prev) => ({ ...prev, [loc]: parseInt(e.target.value) || 1 }))}
                            data-testid={`deco-colors-${itemIndex}-${loc}`} />
                        ) : (
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(decoPrimary === 'embroidery' || Object.values(decoLocationMethods).includes('embroidery')) && (
              <div>
                <Label style={{ fontSize: 12, fontWeight: 600 }}>Stitch count (embroidery)</Label>
                <Input type="number" min={1000} max={20000} step={500} value={decoStitch}
                  onChange={(e) => setDecoStitch(e.target.value)} style={{ marginTop: 6 }}
                  data-testid={`deco-stitch-${itemIndex}`} />
              </div>
            )}
          </div>

          <DialogFooter style={{ flexDirection: 'column', gap: 8, alignItems: 'stretch' }}>
            {!confirmReprice ? (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost" onClick={() => setDecoOpen(false)} disabled={savingDeco}>Cancel</Button>
                <Button onClick={() => setConfirmReprice(true)} disabled={savingDeco}
                  data-testid={`deco-save-${itemIndex}`}>
                  Save
                </Button>
              </div>
            ) : (
              <div style={{ background: '#fef9c3', border: '1px solid #fde68a', padding: 12, borderRadius: 8 }}>
                <p style={{ fontSize: 13, color: '#92400e', margin: 0, marginBottom: 8 }}>
                  Decoration method changed. Re-price this proposal now, or keep the current pricing?
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmReprice(false)} disabled={savingDeco}>Back</Button>
                  <Button variant="outline" size="sm" onClick={() => persistDecoration(false)} disabled={savingDeco}
                    data-testid={`deco-keep-price-${itemIndex}`}>
                    Keep current pricing
                  </Button>
                  <Button size="sm" onClick={() => persistDecoration(true)} disabled={savingDeco}
                    data-testid={`deco-reprice-${itemIndex}`}>
                    {savingDeco ? 'Saving…' : 'Re-price now'}
                  </Button>
                </div>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Color Separation Sheet modal — lazy-mounted so axios isn't hit
          until the admin actually clicks "Color Separations". */}
      {sepOpen && (
        <ColorSeparationSheet
          open={sepOpen}
          onClose={() => setSepOpen(false)}
          proposalId={proposalId}
          itemIndex={itemIndex}
        />
      )}
    </div>
  );
};

/**
 * Per-location Logo/Print Dimensions editor.
 * Renders one row per print_location with a free-text size input
 * ("10\" x 10\"", "3.5in W", "Full chest" etc.). Saves via the new
 * PATCH /proposals/{id}/items/{idx}/print-sizes endpoint.
 *
 * Free-text by design — admins use different conventions and we don't
 * want to constrain them to width/height numbers.
 */
const PrintSizesEditor = ({ item, itemIndex, proposalId, onUpdated }) => {
  const initial = item?.print_sizes || {};
  const locations = Array.isArray(item?.print_locations) && item.print_locations.length > 0
    ? item.print_locations
    : ['front'];
  const [sizes, setSizes] = React.useState(() => {
    const seeded = {};
    locations.forEach((loc) => {
      const key = String(loc).toLowerCase();
      seeded[key] = initial[key] || initial[loc] || '';
    });
    return seeded;
  });
  const [saving, setSaving] = React.useState(false);

  // Re-sync when item changes externally (e.g. after AI edit, location added)
  React.useEffect(() => {
    const seeded = {};
    locations.forEach((loc) => {
      const key = String(loc).toLowerCase();
      seeded[key] = (item?.print_sizes && (item.print_sizes[key] || item.print_sizes[loc])) || '';
    });
    setSizes(seeded);
  }, [item?.print_locations?.length, JSON.stringify(item?.print_sizes || {})]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `/proposals/${proposalId}/items/${itemIndex}/print-sizes`,
        { print_sizes: sizes },
      );
      toast.success('Logo sizes saved');
      onUpdated?.(data.proposal || data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save logo sizes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`print-sizes-${itemIndex}`}
      style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', background: '#f0f9ff' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          📐 Logo / Print Sizes (per location)
        </span>
        <span style={{ fontSize: 10, color: '#0369a1', fontStyle: 'italic' }}>
          Shown to customer on their approval page
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
        {locations.map((loc) => {
          const key = String(loc).toLowerCase();
          return (
            <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span style={{ textTransform: 'capitalize', fontWeight: 600, color: '#0f172a' }}>{loc}</span>
              <input
                type="text"
                placeholder={'e.g. 10" x 10"'}
                value={sizes[key] || ''}
                onChange={(e) => setSizes((prev) => ({ ...prev, [key]: e.target.value }))}
                data-testid={`print-size-input-${itemIndex}-${key}`}
                style={{
                  padding: '6px 8px',
                  border: '1px solid #bae6fd',
                  borderRadius: 6,
                  fontSize: 13,
                  background: '#fff',
                }}
              />
            </label>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          data-testid={`save-print-sizes-${itemIndex}`}
          style={{ background: '#0369a1', color: '#fff' }}
        >
          {saving ? 'Saving…' : 'Save Sizes'}
        </Button>
      </div>
    </div>
  );
};

export default ProductMockupCard;
